import { Injectable } from '@angular/core';
import { TimelineHistoricService } from './timeline-historic.service';
import { TimelineViewStore } from '../state/timeline-view.store';
import { forkJoin, map, Observable, of, tap } from 'rxjs';
import {
  AppointmentService,
  DocumentService,
  EmailDocumentService,
  InteractionLogService,
  NoteService,
  SessionService,
  TaskService,
} from '@maximizer/core/shared/data-access';
import {
  ActivityTimeline,
  ActivityTimelineUpcomingOverdue,
  EntityType,
  ListItem,
  TimelineConfiguration,
  TimelineEvent,
  TimelineHistoricDetailsEvent,
  TimelineHistoricPagination,
  User,
} from '@maximizer/core/shared/domain';
import { ActivityTimelineService } from './activity-timeline.service';
import { clampArray } from '@maximizer/shared/util';
import { TimelineKeys } from '../state/timeline.models';

@Injectable()
export class TimelineViewService {
  static readonly PageSize = 20;

  private _historicNextPageKey?: string;

  constructor(
    private readonly timelineHistoricService: TimelineHistoricService,
    private readonly activityService: ActivityTimelineService,
    private readonly store: TimelineViewStore,
    private readonly sessionService: SessionService,
    private readonly appointmentService: AppointmentService,
    private readonly taskService: TaskService,
    private readonly noteService: NoteService,
    private readonly documentService: DocumentService,
    private readonly emailDocumentService: EmailDocumentService,
    private readonly interactionLog: InteractionLogService,
  ) {}

  initialLoad(): Observable<{
    currentUser: User | undefined;
    configuration: TimelineConfiguration | null;
    users: User[];
  }> {
    if (this.store.currentUser() && this.store.timelineConfiguration()) {
      return of({
        currentUser: this.store.currentUser(),
        configuration: this.store.timelineConfiguration(),
        users: this.store.users(),
      });
    }

    return forkJoin({
      currentUser: this.sessionService.getCurrentUser(),
      configuration: this.timelineHistoricService.getConfiguration(),
      users: this.sessionService.getUsersByPreference(),
    }).pipe(
      tap((data) => {
        this.store.setCurrentUser(data.currentUser);
        this.store.setUsers(data.users);
        this.store.setTimelineFilter({
          ...this.store.searchFilter(),
          users: data.users.map((x) => x.id),
        });

        if (data.configuration) {
          this.store.setTimelineConfiguration(data.configuration);
        }
      }),
    );
  }

  private loadHistoricsWithPagination(
    nextPageKey?: string,
  ): Observable<TimelineEvent[]> {
    const clearStore = !nextPageKey;

    return this.timelineHistoricService
      .forEntryWithPaginationKey(
        this.store.entryTimelineKeys() ?? this.store.entryKey(),
        this.store.entryType(),
        this.store.searchFilter(),
        nextPageKey,
        TimelineViewService.PageSize,
      )
      .pipe(
        map((result) => {
          this._historicNextPageKey = result.nextPageKey;
          this.store.setTimelineEvents(
            clearStore
              ? result.data
              : [...this.store.timelineEvents(), ...result.data],
          );
          return this.store.timelineEvents();
        }),
      );
  }

  hasHistoricNextPageKey(): boolean {
    return !!this._historicNextPageKey;
  }

  loadHistorics(): Observable<TimelineEvent[]> {
    return this.loadHistoricsWithPagination();
  }

  loadHistoricByKey(key: string): Observable<TimelineHistoricPagination> {
    return this.timelineHistoricService.getEntryByKey(key);
  }

  loadHistoricNextPage(): Observable<TimelineEvent[]> {
    if (this._historicNextPageKey) {
      return this.loadHistoricsWithPagination(this._historicNextPageKey);
    }
    return of(this.store.timelineEvents());
  }

  getLeadAndAbEntryKeyFrom(opportunityKey: string): Observable<TimelineKeys> {
    return this.timelineHistoricService.getLeadAndAbEntryKeyFrom(
      opportunityKey,
    );
  }

  loadUpcomingOverdueByKey(
    key: string,
  ): Observable<ActivityTimelineUpcomingOverdue> {
    return this.activityService.getOverdueAndUpcomingByKey(key);
  }

  loadUpcomingOverdue(top?: number): Observable<ActivityTimeline[]> {
    // top +1 so we can know if there are more
    // entries to load, so we display: SHOW MORE button.
    return this.activityService
      .getOverdueAndUpcoming(
        this.store.entryTimelineKeys() ?? this.store.entryKey(),
        this.store.entryType(),
        this.store.searchFilter(),
        top ? top + 1 : top,
      )
      .pipe(
        map((result) => {
          const clampedUpcoming = top
            ? clampArray(result.upcoming, top)
            : result.upcoming;
          const activities = [...result.overdue, ...clampedUpcoming].sort(
            (a, b) => {
              const aTime = new Date(a.date.value).getTime();
              const bTime = new Date(b.date.value).getTime();
              return aTime - bTime;
            },
          );

          this.store.setActivities(activities);
          this.store.setFutureActivitiesQuantity(result.upcoming.length);
          return this.store.activities();
        }),
      );
  }

  loadHistoricDetails(
    key: string,
    type: EntityType,
  ): Observable<TimelineHistoricDetailsEvent | null> {
    return this.timelineHistoricService.forEntryDetails(key, type);
  }

  getNoteFieldOptions(parentKey: string): Observable<ListItem<string>[]> {
    return this.noteService.getNoteFieldOptions(parentKey);
  }

  getAbEntryOrContactKeyFrom(caseKey: string): Observable<TimelineKeys> {
    return this.timelineHistoricService.getAbEntryOrContactKeyFrom(caseKey);
  }

  deleteAppointment(keys: string[]): Observable<boolean> {
    return this.appointmentService.delete(keys);
  }
  deleteTask(keys: string[]): Observable<boolean> {
    return this.taskService.delete(keys);
  }

  deleteNote(keys: string[]): Observable<boolean> {
    return this.noteService.delete(keys);
  }

  deleteDocument(keys: string[]): Observable<boolean> {
    return this.documentService.delete(keys);
  }

  deleteEmailDocument(keys: string[]): Observable<boolean> {
    return this.emailDocumentService.delete(keys);
  }

  deleteInteractionLog(keys: string[]): Observable<boolean> {
    return this.interactionLog.delete(keys);
  }
}
